'use client';

import { fetchSearchResult } from '@/api/product';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { setSession } from '@/utils/tools';
import { DeleteOutlined, HotFilled2, InputDelete } from '@zeelool/icon';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useSearchStore } from './store/search-store';
import { useTranslations } from '@/i18n/use-translations';

interface SearchPartProps {
  type: 'history' | 'hot';
  onClose?: () => void;
  onClickWord?: (item: any, type: string, flag: number) => void;
}

const SearchPart = ({ type, onClose, onClickWord }: SearchPartProps) => {
  const router = useRouter();
  const toJumpTarget = useJump();
  const currency = useStore((state) => state.currency);
  const searchParams = useSearchParams();
  const { trackEvent } = useReporting();
  const [activeIdx, setActiveIdx] = useState(0);
  const isMobile = useStore((state) => state.isMobileScreen);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const t = useTranslations('layout.header.search');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');

  // 从store获取数据
  const searchHistory = useSearchStore((state) => state.searchHistory);
  const setSearchHistory = useSearchStore((state) => state.setSearchHistory);
  const historyProductList = useSearchStore((state) => state.historyProductList);
  const hotProductList = useSearchStore((state) => state.hotProductList);
  const userInfo = useStore((state) => (state as any).userInfo);

  // 根据type决定使用哪个数据源
  const searchList = type === 'history' ? historyProductList : hotProductList;
  const hasRecently = searchHistory.length > 0;

  // 计算商品列表
  const goodsList = (() => {
    let list: any[] = [];
    try {
      list = searchList[activeIdx]?.products || [];
      // 如果有历史搜索，PC和WAP都只展示3个，如果没有历史搜索，WAP展示3个，PC展示6个
      if (hasRecently) {
        list = list.slice(0, 3);
      } else {
        list = list.slice(0, isMobile ? 3 : 6);
      }
    } catch (error) {
      logger.log(error);
    }
    return list;
  })();

  // 搜索埋点标识
  const searchType = type === 'history' ? 2 : 4;

  // 是否展示more按钮
  const showMoreBtn = searchList[activeIdx]?.products?.length || 0;

  // 点击商品
  const tapGoods = (item: any) => {
    const params = {
      search: searchList[activeIdx].keywords,
      page: '1',
      limit: '6',
      // 1普通搜索 2记录搜索 3默认词搜索 4热搜搜索
      search_type: searchType,
    };

    // TODO: 莫名其妙，调用该接口干啥？？
    fetchSearchResult(params);
    setSession('zl_goodsDetailEnterPageScene', '搜索浮层进入');

    if (item.shop_sku) {
      router.push(`/goods-detail/${item.shop_sku}`);
    }

    // 埋点统计
    SDSearchPageProductBtn_Click({
      shop_sku: item.shop_sku,
      business_sku: item.business_sku,
      product_sku: item.sku,
      product_id: `${item.id}`,
      product_name: item.short_name,
    });
    onClose?.();
  };

  // 点击搜索词
  // 有这么几个交互：
  // 移动端下，点击关键词，会触发关键词回调回输入框，并将下面的商品列表更新为点击的关键词
  // PC端下，比较复杂，因为产品要做成点击关键词跳转到指定的页面或者进入搜索结果页，因此我看
  // 原有的vue逻辑在PC端通过鼠标悬浮来切换关键词并更新商品列表，当点击的时候，反而做的是跳转的操作
  // 因此我想聚合PC和M的跳转操作
  const clickWord = (item: any, i: number, itemData: any | undefined) => {
    setActiveIdx(i);
    // emit的1为点击搜索词，用来后续埋点统计
    onClickWord?.(item, type, 1);
    // TODO: 这里需要优化，因为PC和M的跳转逻辑不一样，PC是点击关键词跳转到指定的页面或者进入搜索结果页，M是点击关键词回输入框，并将下面的商品列表更新为点击的关键词
    if (!isMobile) {
      // type为区分历史记录和热搜词（记录搜索2，热词搜索4）
      // 设置搜索历史
      const setHistory = (search: string) => {
        let newSearchHistory = [...searchHistory];

        if (!newSearchHistory.find((it) => it === search)) {
          // 搜索历史中没有当前搜索项直接插入到数组第一项
          newSearchHistory.unshift(search);
          if (newSearchHistory.length > 5) {
            newSearchHistory = newSearchHistory.slice(0, 5);
          }
        } else {
          // 搜索历史中含有当前搜索项,找到索引删除，再插入到第一项
          const index = newSearchHistory.findIndex((ix) => ix === search);
          const itemx = newSearchHistory.splice(index, 1)[0];
          newSearchHistory.unshift(itemx);
        }

        setSearchHistory(newSearchHistory);
      };
      const searchTypeNumber =
        {
          history: 2,
          hot: 4,
        }[type] || 1;
      // 检查是否与当前路由相同
      const currentKeywords = searchParams.get('keywords');
      const currentSearchType = searchParams.get('search_type');
      if (
        currentKeywords &&
        (currentKeywords === item.name || currentKeywords === item) &&
        currentSearchType &&
        +currentSearchType === searchTypeNumber
      ) {
        return;
      }
      if (itemData && itemData?.jump && itemData?.jump?.jump_type === 4) {
        type === 'hot' && setHistory(item.keywords ? item.keywords : item);
        // 使用 useJump 的 toJumpTarget 方法处理指定链接跳转
        toJumpTarget({
          jump_type: itemData.jump.jump_type,
          Jump_target: itemData.jump.path,
        });
      } else if (item.jump && item.jump.id) {
        // ZELL-64382 记录从搜索浮层进入商品列表
        setSession('zl_goodsListEnterPageScene', '搜索浮层进入');
        router.push(`/goods-list/${item.jump.id}`);
      } else if (item) {
        type === 'hot' && setHistory(item.keywords ? item.keywords : item);
        router.push(
          `/search-result?keywords=${item.keywords ? item.keywords : item}&search_type=${searchTypeNumber}`,
        );
      } else {
        type === 'hot' && setHistory(item.keywords ? item.keywords : item);
        router.push(`/search-result?keywords=${item.keywords ? item.keywords : item}`);
      }
      trackEvent(
        {
          eventName: 'SearchBtn_Click',
          properties: {
            search_type:
              {
                history: 'Recently Searched',
                hot: 'Popular Searches',
              }[type] || '普通搜索',
            search_text: item.keywords ? item.keywords : item,
          },
        },
        [ProviderType.SENSORS],
      );
    }
  };

  // 跳转到搜索结果页
  const toSearch = (item: any, itemData: any | undefined) => {
    const searchTypeMap: Record<string, string> = {
      '1': '普通搜索',
      '2': 'Recently Searched',
      '3': '普通搜索',
      '4': 'Popular Searches',
    };

    // 埋点统计
    trackEvent(
      {
        eventName: 'SearchBtn_Click',
        properties: {
          search_type: searchTypeMap[searchType.toString()],
          search_text: item,
        },
      },
      [ProviderType.SENSORS],
    );

    const currentKeywords = searchParams.get('keywords');
    const currentSearchType = searchParams.get('search_type');

    if (
      currentKeywords &&
      (currentKeywords === item.name || currentKeywords === item) &&
      currentSearchType &&
      +currentSearchType === searchType
    ) {
      onClose?.();
      return;
    }
    if (itemData && itemData?.jump && itemData?.jump?.jump_type === 4) {
      toJumpTarget({
        jump_type: itemData.jump.jump_type,
        Jump_target: itemData.jump.path,
      });
      onClose?.();
      return;
    }
    if (item) {
      router.push(`/search-result?keywords=${item}&search_type=${searchType}`);
      onClose?.();
    }
  };

  // 商品点击埋点
  const SDSearchPageProductBtn_Click = (params: any) => {
    try {
      const searchTypeMap: Record<string, string> = {
        '1': '普通搜索',
        '2': 'Recently Searched',
        '3': '普通搜索',
        '4': 'Popular Searches',
      };

      trackEvent(
        {
          eventName: 'SearchPageProductBtn_Click',
          properties: {
            search_type: searchTypeMap[searchType.toString()],
            ...params,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  // 删除搜索历史项
  const deleteItem = (deleteType: 'all' | 'single', item?: string) => {
    if (deleteType === 'all') {
      setSearchHistory([]);
    } else if (item) {
      const newSearchHistory = searchHistory.filter((val) => val !== item);
      setSearchHistory(newSearchHistory);
    }
  };

  // 删除所有历史
  const handleDeleteAll = () => {
    deleteItem('all');
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return searchList.length > 0 ? (
    <div className="w-full lg:w-[560px] mt-[10px] lg:mt-[18px] px-[15px] lg:px-0 py-[20px] lg:py-0 bg-white lg:bg-transparent rounded-[20px] lg:rounded-none">
      <div className={`flex ${isMobile ? 'justify-between' : ''} items-center mb-2.5`}>
        <span className="text-[15px] lg:text-[16px] font-bb lg:font-bold text-[#282828]">
          {type === 'history' ? t('recentlySearched') : t('popularSearches')}
        </span>
        {type === 'history' && (
          <button
            className="bg-transparent border-none cursor-pointer text-gray-400 lg:text-[#999999] lg:ml-[20px] lg:text-[16px] p-1 hover:text-gray-600"
            onClick={handleDeleteAll}
          >
            <DeleteOutlined className="text-base lg:text-[16px]" />
          </button>
        )}
      </div>

      <ul className="flex flex-wrap gap-1.5 lg:gap-0 lg:mt-[18px] mb-[1.867vw] md:mb-0 md:mg-[10px]">
        {searchList.map((item, i) => (
          <li
            key={i}
            className={`flex items-center justify-center bg-[#f5f5f5] lg:bg-[#f4f4f4] rounded-[14px] lg:rounded-[5px] px-[12px] lg:px-[10px] min-w-10 h-[28px] lg:h-[32px] lg:leading-[32px] box-border cursor-pointer transition-all duration-200 border border-transparent lg:border-[#f4f4f4] lg:mr-[10px] lg:mb-[10px] text-[12px] lg:text-[14px] ${
              i === activeIdx
                ? 'text-primary-purple !border-primary-purple !bg-[#e3ebff]'
                : 'text-[#282828]'
            }`}
            onClick={() => clickWord(item.keywords, i, item)}
            // 这里不用判断PC还是WAP，这两个事件只会在PC有
            onMouseEnter={() => {
              timeoutRef.current = setTimeout(() => setActiveIdx(i), 150);
            }}
            onMouseLeave={() => {
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
            }}
          >
            {type === 'hot' && item.is_show === 1 && (
              <HotFilled2 className="text-[#ff2d2d] mr-[4px] text-[16px] lg:align-middle" />
            )}
            <span className="overflow-hidden text-ellipsis line-clamp-1 whitespace-nowrap">
              {item.keywords}
            </span>
            {type === 'history' && (
              <InputDelete
                className="text-[12px] ml-[6px] lg:text-[16px]"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteItem('single', item.keywords);
                }}
              />
            )}
          </li>
        ))}
      </ul>

      <div className="lg:flex lg:justify-between lg:items-center mt-12">
        {goodsList.length === 0 ? (
          <p
            className={`${isMobile ? 'mx-auto text-center' : 'text-left'} mb-[10px] text-[12px] lg:text-[14px] text-gray-600 lg:text-[#333333] leading-relaxed`}
          >
            {t('noSearchResults')} {isMobile ? <br /> : ''}
            <span className="text-gray-800 lg:text-[#333333] font-bold leading-5">
              {searchList[activeIdx] ? searchList[activeIdx].keywords : ''}
            </span>
          </p>
        ) : (
          <ul className="flex flex-wrap lg:grid lg:grid-cols-3 lg:gap-[15px]">
            {goodsList.map((item, i) => (
              <li
                key={i}
                className="w-[103px] lg:w-auto mb-[16px] lg:mb-0 cursor-pointer lg:flex lg:flex-col lg:items-center"
                style={{
                  marginRight: (i + 1) % 3 !== 0 ? '15px' : '0',
                }}
                onClick={() => tapGoods(item)}
              >
                <Image
                  src={item.image?.product?.[0] || item.image_url}
                  alt={item.name || item.short_name}
                  width={103}
                  height={52}
                  className={`w-[103px] lg:w-full lg:max-w-[147px] object-contain mb-[6px] lg:mb-[4px] rounded ${
                    item.is_glass === 2 ? 'border border-gray-200 lg:border-[#eeeeee]' : ''
                  }`}
                />
                <div className="text-center lg:mt-[4px] lg:flex lg:justify-center lg:h-[40px] lg:items-center">
                  <div className="flex flex-col items-center lg:flex lg:justify-center lg:flex-row">
                    <span
                      className={`text-[14px] text-gray-800 lg:font-bold whitespace-nowrap ${
                        priceInfo(userInfo, item, currency).price ===
                        priceInfo(userInfo, item, currency).sell_price
                          ? 'lg:text-[#333333]'
                          : 'lg:text-[#f5513a]'
                      }`}
                      aria-label={
                        tPriceA11y('srCurrent') +
                        formatPriceNumberWithSymbol(
                          priceInfo(userInfo, item, currency).sell_price,
                          currency,
                        )
                      }
                    >
                      {formatPriceNumberWithSymbol(
                        priceInfo(userInfo, item, currency).sell_price,
                        currency,
                      )}
                    </span>
                    {priceInfo(userInfo, item, currency).price !==
                      priceInfo(userInfo, item, currency).sell_price && (
                      <span className="text-[14px] line-through text-[#a9a9a9] lg:text-[#999999] lg:ml-[5px] whitespace-nowrap">
                        <del
                          aria-label={
                            tPriceA11y('srOriginal') +
                            formatPriceNumberWithSymbol(
                              priceInfo(userInfo, item, currency).price,
                              currency,
                            )
                          }
                        >
                          {formatPriceNumberWithSymbol(
                            priceInfo(userInfo, item, currency).price,
                            currency,
                          )}
                        </del>
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {/* pc端和wap端不同，如果有历史搜索，大于三个展示more按钮，如果没有历史搜索，大于等于6个展示more按钮 */}
        {((hasRecently && showMoreBtn > 3) ||
          (!hasRecently && ((!isMobile && showMoreBtn >= 6) || (isMobile && showMoreBtn > 3)))) && (
          <p
            className="flex items-center justify-center w-[150px] h-[34px] mx-auto mt-[5px] lg:relative lg:top-[-10px] lg:cursor-pointer lg:ml-[40px] lg:w-auto lg:h-auto lg:mx-0 lg:mt-0 border border-black lg:border-none rounded cursor-pointer text-[12px] lg:text-[14px] transition-all duration-200 hover:bg-gray-50 lg:hover:bg-transparent font-bold"
            onClick={() => toSearch(searchList[activeIdx].keywords, searchList[activeIdx])}
          >
            {!isMobile ? t('more') : t('viewMore')}
          </p>
        )}
      </div>
    </div>
  ) : null;
};

export default SearchPart;
